import { useState } from 'react';
import Field from '../components/Field';
import Icon from '../components/Icon';
import StatusBadge from '../components/StatusBadge';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import useForm from '../hooks/useForm';
import * as v from '../validation/rules';
import { PASSWORD_CODES, UPLOAD_CODES } from '../validation/serverCodes';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function ProfilePage() {
  const { user, logout, refreshProfile } = useAuth();
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const passwordForm = useForm({
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    validators: {
      currentPassword: v.currentPassword,
      newPassword: v.password,
      confirmPassword: v.required('Confirmation'),
    },
    crossField: (values) => {
      if (values.confirmPassword && values.newPassword !== values.confirmPassword) {
        return { confirmPassword: 'Passwords do not match' };
      }
      if (values.newPassword && values.newPassword === values.currentPassword) {
        return { newPassword: 'Choose a password different from your current one' };
      }
      return {};
    },
  });

  // The existing document means a replacement is optional here, unlike registration.
  const uploadForm = useForm({
    initialValues: { idProof: null },
    validators: { idProof: v.idProof({ requiredFile: true }) },
  });

  const changePassword = passwordForm.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      setToast('Password changed — signing you out of all devices');
      setTimeout(logout, 1200);
    } catch (err) {
      if (!passwordForm.applyServerErrors(err, PASSWORD_CODES)) setError(err);
    }
  });

  const uploadIdProof = uploadForm.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.uploadIdProof(values.idProof);
      await refreshProfile();
      uploadForm.reset({ idProof: null });
      setToast('ID proof uploaded — Support will review it');
    } catch (err) {
      if (!uploadForm.applyServerErrors(err, UPLOAD_CODES)) setError(err);
    }
  });

  const teacher = user?.teacher;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Profile &amp; Settings</h1>
          <p>Your account details, password and the devices currently signed in.</p>
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {user?.mustChangePassword && (
        <InfoBanner>You are signed in with a temporary password. Set your own password below.</InfoBanner>
      )}

      <section className="card card-pad">
        <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>Account</h2>
        <div className="grid-2">
          <Field label="Full name" id="p-name">
            <input id="p-name" className="input" value={user?.fullName ?? ''} readOnly />
          </Field>
          <Field label="Role" id="p-role" hint="Set at creation and not user-editable">
            <input id="p-role" className="input" value={user?.roleLabel ?? ''} readOnly />
          </Field>
          <Field label="Mobile" id="p-mobile" hint={user?.mobileVerified ? 'Verified' : 'Not verified yet'}>
            <input id="p-mobile" className="input" value={user?.mobile ?? ''} readOnly />
          </Field>
          <Field label="Email" id="p-email">
            <input id="p-email" className="input" value={user?.email ?? '—'} readOnly />
          </Field>
        </div>
      </section>

      {teacher && (
        <section className="card card-pad">
          <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s0)' }}>Verification</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            Status <StatusBadge status={teacher.verificationStatus} />
            {teacher.rejectionReason ? ` — ${teacher.rejectionReason}` : ''}
          </p>
          <form {...uploadForm.formProps} onSubmit={uploadIdProof} className="stack">
            <Field
              label={teacher.idProofUploaded ? 'Replace ID proof' : 'Upload ID proof'}
              id="idProof"
              hint={v.LIMITS.idProof.label}
              error={uploadForm.errorFor('idProof')}
            >
              <input
                id="idProof"
                name="idProof"
                className="input"
                type="file"
                accept={v.LIMITS.idProof.types.join(',')}
                aria-invalid={uploadForm.showError('idProof') ? 'true' : undefined}
                aria-describedby={uploadForm.showError('idProof') ? 'idProof-error' : undefined}
                onChange={(event) => uploadForm.setFieldValue('idProof', event.target.files?.[0] ?? null)}
              />
            </Field>
            <div>
              <button type="submit" className="btn btn-secondary" disabled={uploadForm.submitting}>
                <Icon name="upload" size={15} />
                {uploadForm.submitting ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="card card-pad">
        <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>Change password</h2>
        <form {...passwordForm.formProps} onSubmit={changePassword} className="stack" style={{ maxWidth: 420 }}>
          <Field label="Current password" id="currentPassword" required
                 error={passwordForm.errorFor('currentPassword')}>
            <input className="input" type="password" autoComplete="current-password" maxLength={100}
                   {...passwordForm.field('currentPassword')} />
          </Field>
          <Field label="New password" id="newPassword" required hint={v.PASSWORD_HINT}
                 error={passwordForm.errorFor('newPassword')}>
            <input className="input" type="password" autoComplete="new-password" maxLength={100}
                   {...passwordForm.field('newPassword')} />
          </Field>
          <Field label="Confirm new password" id="confirmPassword" required
                 error={passwordForm.errorFor('confirmPassword')}>
            <input className="input" type="password" autoComplete="new-password" maxLength={100}
                   {...passwordForm.field('confirmPassword')} />
          </Field>
          <div>
            <button type="submit" className="btn btn-primary" disabled={passwordForm.submitting}>
              {passwordForm.submitting ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </section>

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}
